'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '../i18n/navigation';
import { routing } from '../i18n/routing';
import { FLAG_COMPONENTS } from './FlagIcons';

const STORAGE_KEY = 'enqdigital_language_suggest_dismissed';

// Mapa idioma do navegador -> locale do site. navigator.language costuma vir
// como "en-US", "es-AR" etc: comparamos só o prefixo antes do hífen. Qualquer
// idioma do navegador fora de {en, es} (inclui pt/pt-BR e qualquer outro) não
// gera sugestão — o site já está em português por padrão para esses casos.
const BROWSER_TO_LOCALE: Record<string, 'en' | 'es'> = {
  en: 'en',
  es: 'es',
};

const LOCALE_NAME: Record<string, string> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
};

/**
 * Sugestão client-side de idioma, nunca redirect. Detecta o idioma do
 * navegador (Accept-Language via navigator.language) e, se divergir do
 * locale atual da rota, mostra um banner discreto oferecendo trocar — o
 * visitante decide, nunca é movido automaticamente.
 *
 * Deliberadamente client-only e sem qualquer efeito no HTML servido a
 * crawlers: um middleware/redirect por Accept-Language já causou problema de
 * indexação neste site antes (ver CLAUDE.md, alternateLinks/hreflang) porque
 * confunde o Google sobre qual é a página canônica. Um banner que só existe
 * depois da hidratação no browser do visitante não tem esse risco - Googlebot
 * nunca envia Accept-Language: en/es esperando ver o site mudar de URL, e
 * mesmo que enviasse, o servidor sempre responde com o mesmo HTML/canonical
 * de antes.
 */
export default function LanguageSuggestBanner() {
  const t = useTranslations('idiomaSugestao');
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [suggested, setSuggested] = useState<'en' | 'es' | null>(null);
  // Empilha acima do banner de cookies (#cookie-consent-banner) quando os
  // dois aparecem juntos, em vez de um cobrir o outro - ambos são
  // position:fixed/bottom:0. Medido dinamicamente (não um valor fixo
  // chutado) porque a altura do aviso de cookies varia com o tamanho da
  // tela (o texto quebra em mais linhas em telas estreitas).
  const [bottomOffset, setBottomOffset] = useState(0);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return;

      const browserLang = (navigator.language || '').split('-')[0].toLowerCase();
      const target = BROWSER_TO_LOCALE[browserLang];
      if (!target || target === locale) return;
      if (!routing.locales.includes(target)) return;

      setSuggested(target);
    } catch {
      // navigator/localStorage indisponível (SSR guard extra) - não sugere
    }
    // Reavalia só quando a rota muda de locale de fato, não a cada navegação
    // interna no mesmo idioma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (!suggested) return;

    function measure() {
      const cookieBanner = document.getElementById('cookie-consent-banner');
      setBottomOffset(cookieBanner ? cookieBanner.getBoundingClientRect().height : 0);
    }

    measure();

    // O CookieConsent monta/desmonta condicionalmente (aceitar remove do DOM)
    // e sua altura muda com o wrap de texto ao redimensionar a janela -
    // ResizeObserver cobre ambos os casos sem precisar de um evento próprio.
    const cookieBanner = document.getElementById('cookie-consent-banner');
    if (!cookieBanner) return;
    const observer = new ResizeObserver(measure);
    observer.observe(cookieBanner);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [suggested]);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage indisponível; apenas oculta nesta sessão
    }
    setSuggested(null);
  }

  function accept() {
    if (!suggested) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage indisponível; segue com a troca mesmo assim
    }
    router.replace(pathname, { locale: suggested });
    setSuggested(null);
  }

  if (!suggested) {
    return null;
  }

  const Flag = FLAG_COMPONENTS[suggested];

  return (
    <div
      role="dialog"
      aria-label={t('ariaLabel')}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: bottomOffset,
        zIndex: 99,
        backgroundColor: '#0f2740',
        color: '#ffffff',
        borderTop: '1px solid rgba(255,255,255,0.15)',
        transition: 'bottom 0.15s',
      }}
    >
      <div
        className="container-site"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '14px 24px',
        }}
      >
        <p
          style={{
            fontSize: '13px',
            color: '#e6edf5',
            margin: 0,
            flex: '1 1 320px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {Flag && <Flag />}
          <span>{t('mensagem', { idioma: LOCALE_NAME[suggested] })}</span>
        </p>
        <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
          <button
            onClick={dismiss}
            style={{
              backgroundColor: 'transparent',
              color: '#c7d4e0',
              fontSize: '13px',
              fontWeight: 600,
              padding: '10px 16px',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.25)',
              cursor: 'pointer',
            }}
          >
            {t('dispensar')}
          </button>
          <button
            onClick={accept}
            style={{
              backgroundColor: '#1a9fff',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 600,
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t('trocar', { idioma: LOCALE_NAME[suggested] })}
          </button>
        </div>
      </div>
    </div>
  );
}
